import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import { Button } from './button';
import { cn } from '../../lib/utils';

export default function Dialog({ isOpen, onClose, title, children, className, contentRef }) {
  const ref = useRef(null);
  const titleId = useId();

  useEffect(() => {
    if (!isOpen) return;
    const dialog = ref.current;
    const previousFocus = document.activeElement;
    const overflow = document.body.style.overflow;
    dialog.querySelector('button').focus();
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const focusEdge = (last = false) => {
    const controls = [...ref.current.querySelectorAll('button, a[href], input, select, textarea, iframe, [tabindex]')]
      .filter(element => !element.hasAttribute('data-focus-guard') && !element.disabled && element.tabIndex >= 0 && element.getClientRects().length);
    (last ? controls[controls.length - 1] : controls[0])?.focus();
  };

  return createPortal(<div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/50 p-4" onClick={event => {
    if (event.target === event.currentTarget) onClose();
  }}>
  <section
    ref={ref}
    role="dialog"
    aria-modal="true"
    aria-labelledby={titleId}
    className={cn('relative flex max-h-[calc(100dvh-2rem)] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-solid border-slate-200 bg-white p-0 text-left text-slate-700 shadow-xl', className)}
    onKeyDown={event => {
      // Only contain this dialog's keyboard events, not Stripe's external overlays.
      if (!ref.current.contains(document.activeElement)) return;
      if (event.key === 'Escape') { event.preventDefault(); onClose(); }
    }}
  >
    <span tabIndex={0} data-focus-guard className="sr-only" onFocus={() => focusEdge(true)} />
    <header className="flex shrink-0 items-center justify-between gap-3 border-0 border-b border-solid border-slate-200 p-4 sm:px-6">
      <h2 id={titleId} className="m-0 text-xl font-bold leading-snug text-slate-800">{title}</h2>
      <Button type="button" variant="neutral" size="action" className="m-0 w-12 border-solid p-0" aria-label="Close dialog" title="Close" onClick={onClose}><FaTimes aria-hidden="true" /></Button>
    </header>
    <div ref={contentRef} className="min-h-0 overflow-y-auto overscroll-contain p-4 sm:p-6">{children}</div>
    <span tabIndex={0} data-focus-guard className="sr-only" onFocus={() => focusEdge()} />
  </section>
  </div>, document.body);
}
